import React, { useState } from 'react';
import { 
  EnhancedButton, 
  EnhancedCard, 
  EnhancedInput, 
  FloatingActionButton, 
  ProgressBar 
} from '../components/UI/EnhancedComponents';
import { 
  AnimatedContainer, 
  StaggeredList, 
  HoverAnimation, 
  Pulse, 
  LoadingAnimation, 
  PageTransition 
} from '../components/UI/Animations';
import SkeletonLoader, { 
  TableSkeleton, 
  CardSkeleton, 
  ListSkeleton, 
  ProfileSkeleton, 
  DashboardSkeleton 
} from '../components/UI/SkeletonLoader';
import ErrorState, { 
  NetworkError, 
  NotFoundError, 
  EmptyState, 
  ServerError 
} from '../components/UI/ErrorState';
import {
  SparklesIcon,
  HeartIcon,
  StarIcon,
  BoltIcon,
  FireIcon,
  RocketLaunchIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const UIPolishDemo: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [progress, setProgress] = useState(75);

  const demoCards = [
    { title: 'Enhanced Animations', icon: SparklesIcon, color: 'primary' },
    { title: 'Micro-interactions', icon: HeartIcon, color: 'red' },
    { title: 'Loading States', icon: BoltIcon, color: 'amber' },
    { title: 'Error Handling', icon: XMarkIcon, color: 'gray' },
  ];

  return (
    <PageTransition className="min-h-screen bg-gradient-to-br from-gray-50 to-white p-8">
      <div className="max-w-7xl mx-auto space-y-12">
        
        {/* Header */}
        <AnimatedContainer animation="slideDown" delay={100}>
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              UI/UX Polish Showcase
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the enhanced micro-interactions, animations, and responsive design improvements for Phase 7.1
            </p>
          </div>
        </AnimatedContainer>

        {/* Enhanced Buttons Section */}
        <AnimatedContainer animation="fadeIn" delay={200}>
          <EnhancedCard>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <SparklesIcon className="w-8 h-8 mr-3 text-primary-500" />
              Enhanced Buttons with Ripple Effects
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <EnhancedButton 
                variant="primary" 
                leftIcon={<RocketLaunchIcon className="w-4 h-4" />}
                onClick={() => setLoading(!loading)}
              >
                Primary
              </EnhancedButton>
              
              <EnhancedButton 
                variant="secondary"
                rightIcon={<StarIcon className="w-4 h-4" />}
              >
                Secondary
              </EnhancedButton>
              
              <EnhancedButton 
                variant="success"
                loading={loading}
              >
                Success
              </EnhancedButton>
              
              <EnhancedButton 
                variant="danger"
                size="lg"
              >
                Danger
              </EnhancedButton>
            </div>
          </EnhancedCard>
        </AnimatedContainer>

        {/* Enhanced Cards with Hover Effects */}
        <AnimatedContainer animation="slideUp" delay={300}>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Enhanced Cards & Hover Animations
          </h2>
          
          <StaggeredList staggerDelay={100} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {demoCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <HoverAnimation key={index} animation="lift" intensity="medium">
                  <EnhancedCard clickable onClick={() => console.log(`Clicked ${card.title}`)}>
                    <div className="text-center space-y-4">
                      <div className={`w-16 h-16 mx-auto rounded-xl bg-${card.color}-100 flex items-center justify-center`}>
                        <Icon className={`w-8 h-8 text-${card.color}-600`} />
                      </div>
                      <h3 className="font-semibold text-gray-900">{card.title}</h3>
                      <p className="text-sm text-gray-600">
                        Experience smooth transitions and micro-interactions
                      </p>
                    </div>
                  </EnhancedCard>
                </HoverAnimation>
              );
            })}
          </StaggeredList>
        </AnimatedContainer>

        {/* Enhanced Inputs */}
        <AnimatedContainer animation="slideLeft" delay={400}>
          <EnhancedCard>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Enhanced Form Inputs
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <EnhancedInput
                label="Email Address"
                type="email"
                placeholder="Enter your email"
                value={inputValue}
                onChange={setInputValue}
                icon={<SparklesIcon className="w-5 h-5" />}
                required
              />
              
              <EnhancedInput
                label="Password"
                type="password"
                placeholder="Enter password"
                error={showError ? "Password is required" : undefined}
              />
            </div>
            
            <div className="mt-4 flex space-x-4">
              <EnhancedButton 
                variant="ghost" 
                onClick={() => setShowError(!showError)}
              >
                Toggle Error State
              </EnhancedButton>
            </div>
          </EnhancedCard>
        </AnimatedContainer>

        {/* Progress Bars */}
        <AnimatedContainer animation="slideRight" delay={500}>
          <EnhancedCard>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Animated Progress Indicators
            </h2>
            
            <div className="space-y-6">
              <ProgressBar 
                value={progress} 
                color="primary" 
                showLabel 
                animated 
              />
              
              <ProgressBar 
                value={85} 
                color="success" 
                size="lg"
                showLabel 
              />
              
              <ProgressBar 
                value={45} 
                color="warning" 
                size="sm"
              />
              
              <div className="flex space-x-4">
                <EnhancedButton 
                  variant="secondary" 
                  size="sm"
                  onClick={() => setProgress(Math.max(0, progress - 10))}
                >
                  Decrease
                </EnhancedButton>
                <EnhancedButton 
                  variant="secondary" 
                  size="sm"
                  onClick={() => setProgress(Math.min(100, progress + 10))}
                >
                  Increase
                </EnhancedButton>
              </div>
            </div>
          </EnhancedCard>
        </AnimatedContainer>

        {/* Loading States */}
        <AnimatedContainer animation="scale" delay={600}>
          <EnhancedCard>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Enhanced Loading States
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Skeleton Loaders */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700">Skeleton Loaders</h3>
                <CardSkeleton showAvatar />
              </div>
              
              {/* Loading Animations */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700">Loading Animations</h3>
                <div className="flex items-center space-x-4">
                  <LoadingAnimation type="spinner" />
                  <LoadingAnimation type="dots" />
                  <LoadingAnimation type="bars" />
                </div>
              </div>
              
              {/* Pulse Effects */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700">Pulse Effects</h3>
                <Pulse color="primary">
                  <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center">
                    <HeartIcon className="w-8 h-8 text-primary-600" />
                  </div>
                </Pulse>
              </div>
            </div>
          </EnhancedCard>
        </AnimatedContainer>

        {/* Error States */}
        <AnimatedContainer animation="fadeIn" delay={700}>
          <EnhancedCard>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Enhanced Error States
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <NetworkError 
                title="Connection Lost"
                message="Check your internet connection and try again."
                onRetry={() => console.log('Retrying...')}
              />
              
              <EmptyState 
                title="No Data Found"
                message="Start by adding your first item."
                actionLabel="Add Item"
                onAction={() => console.log('Adding item...')}
              />
            </div>
          </EnhancedCard>
        </AnimatedContainer>

        {/* Responsive Showcase */}
        <AnimatedContainer animation="slideUp" delay={800}>
          <EnhancedCard>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Responsive Design Enhancements
            </h2>
            
            <div className="space-y-4">
              <p className="text-gray-600">
                All components are optimized for different screen sizes with:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 bg-primary-50 rounded-lg">
                  <h4 className="font-semibold text-primary-900">Mobile First</h4>
                  <p className="text-sm text-primary-700">Optimized touch targets and spacing</p>
                </div>
                
                <div className="p-4 bg-secondary-50 rounded-lg">
                  <h4 className="font-semibold text-secondary-900">Tablet Ready</h4>
                  <p className="text-sm text-secondary-700">Perfect for medium screen devices</p>
                </div>
                
                <div className="p-4 bg-accent-50 rounded-lg">
                  <h4 className="font-semibold text-accent-900">Desktop Enhanced</h4>
                  <p className="text-sm text-accent-700">Rich hover effects and interactions</p>
                </div>
              </div>
            </div>
          </EnhancedCard>
        </AnimatedContainer>

        {/* Floating Action Button */}
        <FloatingActionButton 
          onClick={() => console.log('FAB clicked!')}
          color="primary"
          size="lg"
        >
          <PlusIcon className="w-6 h-6" />
        </FloatingActionButton>

      </div>
    </PageTransition>
  );
};

export default UIPolishDemo;
